import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2, Layers } from "lucide-react";
import { Section, SectionHeader } from "./shared";
import { useLanguage } from "@/hooks/useLanguage";
import { uiT } from "@/i18n/ui";
import type { SolutionData } from "@/data/solutionsData";

interface Props {
  solution: SolutionData;
}

const SolutionTabbedServices = ({ solution }: Props) => {
  const [active, setActive] = useState(0);
  const lang = useLanguage();
  const t = uiT(lang);
  const tabs = solution.tabbedServices;
  if (!tabs) return null;

  const current = tabs.tabs[active];

  return (
    <Section>
      <SectionHeader
        label={({ en: solution.category === "pharma" ? "Pharma Operations" : "Device Operations", es: solution.category === "pharma" ? "Operaciones farmacéuticas" : "Operaciones de dispositivos", "zh-cn": solution.category === "pharma" ? "制药运营支持" : "器械运营支持" }[lang])}
        title={tabs.title}
        icon={Layers}
      />
      {tabs.subtitle && (
        <p className="text-muted-foreground text-center max-w-2xl mx-auto mb-10 leading-relaxed">
          {tabs.subtitle}
        </p>
      )}

      <div className="flex justify-center mb-12">
        <div className="inline-flex rounded-full border border-border bg-muted/30 p-1 gap-1">
          {tabs.tabs.map((tab, i) => (
            <button
              key={tab.label}
              onClick={() => setActive(i)}
              className={`relative px-6 py-2.5 rounded-full text-sm font-semibold transition-all duration-300 ${
                active === i
                  ? "bg-primary text-primary-foreground shadow-md"
                  : "text-foreground/60 hover:text-foreground/80 hover:bg-muted/50"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={active}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -12 }}
          transition={{ duration: 0.3 }}
          className="max-w-4xl mx-auto"
        >
          <div className="bg-secondary/40 rounded-2xl p-8 sm:p-10">
            <h3 className="text-xl sm:text-2xl font-bold text-foreground mb-3">
              {current.label}
            </h3>
            {current.intro && (
              <div className="text-sm sm:text-base text-muted-foreground leading-relaxed mb-8 space-y-3">
                {current.intro.split("\n\n").map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
              </div>
            )}

            {current.leadIn && (
              <p className="text-sm text-muted-foreground mb-6 italic">
                {current.leadIn}
              </p>
            )}

            <div className="space-y-5">
              {current.items.map((item, j) => (
                <div key={j} className="flex items-start gap-3">
                  <CheckCircle2
                    size={18}
                    className="text-primary shrink-0 mt-0.5"
                  />
                  <div>
                    <span className="font-semibold text-foreground text-sm">
                      {item.title}
                    </span>
                    {item.title.endsWith(":") ? " " : ": "}
                    <span className="text-sm text-muted-foreground">
                      {item.description}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      </AnimatePresence>
    </Section>
  );
};

export default SolutionTabbedServices;
